import React, { useState, useEffect, useRef } from 'react';
import CircularLoader from '@common/CircularLoader';
import Alert from '@common/Alert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import questionsTheme from './questionsTheme';

const GeoGebraEmbed = ({
                           appletId,
                           minHeight = 600,
                           showToolbar = true,
                           showAlgebraInput = true,
                           showMenuBar = false,
                           enableRightClick = true,
                           enableShiftDragZoom = true,
                           showResetIcon = true,
                           allowStyleBar = true,
                           onLoad,
                           className = '',
                           showOpenButton = true
                       }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const containerRef = useRef(null);

    const ggbId = `ggb-element-${appletId}`;
    const containerClass = `ggb-container-${appletId}`;

    useEffect(() => {
        if (!appletId) {
            setError('No applet ID provided');
            setLoading(false);
            return;
        }

        const initGeoGebra = () => {
            if (!window.GGBApplet) {
                setError('Failed to load GeoGebra API');
                setLoading(false);
                return;
            }

            const currentWidth = containerRef.current?.clientWidth || 800;
            const currentHeight = containerRef.current?.clientHeight || minHeight;

            const parameters = {
                "id": `applet_${appletId}`,
                "material_id": appletId,
                "width": currentWidth,
                "height": currentHeight,
                "borderColor": "#FFFFFF", // Fix 1: Removes the conflicting inner gray border
                "showToolBar": showToolbar,
                "showAlgebraInput": showAlgebraInput,
                "showMenuBar": showMenuBar,
                "enableRightClick": enableRightClick,
                "enableShiftDragZoom": enableShiftDragZoom,
                "showResetIcon": showResetIcon,
                "allowStyleBar": allowStyleBar,
                "useBrowserForJS": false,
                "scaleContainerClass": containerClass,
                "allowUpscale": true,
                "appletOnLoad": () => {
                    setLoading(false);
                    if (onLoad) onLoad();
                }
            };

            const applet = new window.GGBApplet(parameters, true);
            applet.inject(ggbId);
        };

        const scriptId = 'geogebra-deploy-script';

        if (!document.getElementById(scriptId)) {
            const script = document.createElement('script');
            script.id = scriptId;
            script.src = 'https://www.geogebra.org/apps/deployggb.js';
            script.async = true;
            script.onload = initGeoGebra;
            script.onerror = () => {
                setError('Failed to load GeoGebra script');
                setLoading(false);
            };
            document.head.appendChild(script);
        } else {
            if (window.GGBApplet) {
                initGeoGebra();
            } else {
                document.getElementById(scriptId).addEventListener('load', initGeoGebra);
            }
        }

        return () => {
            if (containerRef.current) {
                containerRef.current.innerHTML = '';
            }
        };
    }, [appletId, showToolbar, showAlgebraInput, showMenuBar, enableRightClick, enableShiftDragZoom, showResetIcon, allowStyleBar, minHeight, onLoad, ggbId, containerClass]);

    if (error) {
        return <Alert type="error" title="GeoGebra Error" message={error} />;
    }

    return (
        <div
            className={`ch-geogebra-embed ${containerClass} ${className}`}
            style={{
                position: 'relative',
                width: '100%',
                minHeight: `${minHeight}px`,
                borderRadius: '8px',
                overflow: 'hidden',
                padding: '4px', // Fix 2: Prevents the square UI from hitting the rounded corners
                boxSizing: 'border-box', // Fix 3: Ensures padding doesn't break the 100% width calculation
                border: `1px solid ${questionsTheme?.unmarkedGray || '#e0e0e0'}`,
                backgroundColor: '#ffffff'
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#ffffff',
                    zIndex: 10,
                    opacity: loading ? 1 : 0,
                    visibility: loading ? 'visible' : 'hidden',
                    transition: 'opacity 0.3s ease, visibility 0.3s ease'
                }}
            >
                <CircularLoader displayInElement={true} />
            </div>

            {showOpenButton && (
                <button
                    onClick={() => window.open(`https://www.geogebra.org/m/${appletId}`, '_blank')}
                    className="ch-button ch-button-icon-l ch-btn-outline"
                    aria-label="Open GeoGebra applet in a new tab"
                    style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        zIndex: 20,
                        backgroundColor: 'rgba(255, 255, 255, 0.6)',
                        backdropFilter: 'blur(4px)',
                        border: '1px solid rgba(0,0,0,0.1)',
                        padding: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.95)')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.6)')}
                    title="Open in new tab"
                >
                    <OpenInNewIcon style={{ fontSize: '18px', color: '#4a4a4a' }} />
                </button>
            )}

            <div
                id={ggbId}
                ref={containerRef}
                style={{ width: '100%', height: '100%' }}
            />
        </div>
    );
};

export default GeoGebraEmbed;